import { useEffect, useMemo, useState } from 'react'

import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import ReeferAlarmDetails, { type ReeferAlarmDetailsParams } from '@/insights/alarm/details/ReeferAlarmDetails'
import AlertsDetail, { type AlertsDetailParams } from '@/insights/alert/alertsDetail/AlertsDetail'
import DoorActivities from '@/insights/assets/doorActivities/DoorActivities'
import LocationHistory from '@/insights/assets/locationHistory/LocationHistory'
import ReeferFridgeDataHistory from '@/insights/assets/reeferFridgeDataHistory/ReeferFridgeDataHistory'
import ReeferOperationHistory from '@/insights/assets/reeferOperationHistory/ReeferOperationHistory'
import {
  type AssetWindowParams,
  type AssetWindowState,
  AssetWindowNav,
  assetWindowParams,
} from '@/insights/common/assetHistoryWindow'
import TemperatureHistory from '@/insights/temperature/history/TemperatureHistory'
import {
  type InsightConfigurationType,
  type InsightDefinition,
  type InsightParametersType,
  type InsightStatus,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

/** The parts of an insight this tab uses. */
type AssetHistoryDefinition<P, D> = Pick<
  InsightDefinition<P, D>,
  'slug' | 'defaultWidth' | 'defaultAspectRatio' | 'load' | 'Display'
> & { configuration: { isValid: (parameters: P) => boolean | string } }

interface Result<D> {
  status: InsightStatus
  data?: D
}

/**
 * One insight for the asset over the period, loaded through its own `load` and drawn by its
 * own `Display`, so it shows exactly what a dashboard card would. `Display` leaves the empty
 * and error states to the dashboard's card, so they're drawn here instead.
 */
const AssetInsightBody = <P extends InsightParametersType, D>({
  definition,
  title,
  emptyMessage,
  windowParams,
  toParams,
}: {
  definition: AssetHistoryDefinition<P, D>
  title: string
  emptyMessage: string
  windowParams: AssetWindowParams
  toParams: (windowParams: AssetWindowParams) => P
}) => {
  const { api } = useApi()
  const params = useMemo(() => toParams(windowParams), [toParams, windowParams])
  const [result, setResult] = useState<Result<D>>({ status: { code: InsightStatusCode.LOADING } })
  const { Display } = definition

  const configuration = useMemo<InsightConfigurationType>(
    () => ({
      id: `asset-history-${definition.slug}`,
      slug: definition.slug,
      title,
      shared: false,
      width: definition.defaultWidth,
      aspectRatio: definition.defaultAspectRatio,
      parameters: params,
      displayParameters: {},
    }),
    [definition, title, params]
  )

  useEffect(() => {
    if (!api) return
    // The insights cap a custom range (most at 30 days); the date picker doesn't.
    const valid = definition.configuration.isValid(params)
    if (valid !== true) {
      setResult({ status: { code: InsightStatusCode.NOT_CONFIGURED, message: valid || undefined } })
      return
    }
    let cancelled = false
    setResult((previous) => ({ ...previous, status: { code: InsightStatusCode.LOADING } }))
    definition
      .load(api, params)
      .then((response) => {
        if (cancelled) return
        if (isInsightStatus(response)) {
          setResult({ status: response })
        } else if (Array.isArray(response) && response.length === 0) {
          // The loads only report NO_DATA for a missing body; an empty list is no data too,
          // and `Display` draws nothing for it.
          setResult({ status: { code: InsightStatusCode.NO_DATA } })
        } else {
          setResult({ status: { code: InsightStatusCode.DISPLAYING }, data: response })
        }
      })
      .catch((ex: unknown) => {
        if (cancelled) return
        const message = ex instanceof Error ? ex.message : undefined
        setResult({ status: { code: InsightStatusCode.ERROR, message } })
      })
    return () => {
      cancelled = true
    }
  }, [api, definition, params])

  switch (result.status.code) {
    case InsightStatusCode.NOT_CONFIGURED:
      return <Alert type="info" message={result.status.message ?? 'Choose a shorter period.'} />
    case InsightStatusCode.NO_DATA:
      return <Alert type="info" message={emptyMessage} />
    case InsightStatusCode.ERROR:
      return <Alert type="error" message={result.status.message ?? `Could not load ${title}.`} />
    default:
      return <Display configuration={configuration} status={result.status} data={result.data} />
  }
}

interface HistoryOption {
  label: string
  slug: string
  render: (params: AssetWindowParams) => React.ReactNode
}

/**
 * Closes over each insight's parameter and data types, so the list can hold insights of
 * different shapes. `toParams` turns the asset and period into what the insight asks for.
 */
const option = <P extends InsightParametersType, D>(
  label: string,
  definition: AssetHistoryDefinition<P, D>,
  toParams: (windowParams: AssetWindowParams) => P,
  emptyMessage = 'No data for this period.'
): HistoryOption => ({
  label,
  slug: definition.slug,
  render: (windowParams) => (
    <AssetInsightBody
      definition={definition}
      title={label}
      emptyMessage={emptyMessage}
      windowParams={windowParams}
      toParams={toParams}
    />
  ),
})

/** Most of the insights take the asset and period as they are. */
const asIs = (windowParams: AssetWindowParams) => windowParams

/** The alarm code filter's "select all": every code, without binding them all. */
const ALL_ALARM_CODES = -1

/** Every alarm code and, with no severity filter, every severity. */
const alarmParams = ({ assetId, ...rest }: AssetWindowParams): ReeferAlarmDetailsParams => ({
  ...rest,
  assetIds: [Number(assetId)],
  codeId: [ALL_ALARM_CODES],
})

/**
 * Every alert name and severity, raised or cleared: what the Alert Count by Asset drilldown
 * asks for. As a drilldown it needs neither filter; without them the API applies none.
 */
const alertParams = ({ assetId, ...rest }: AssetWindowParams): AlertsDetailParams => ({
  ...rest,
  assetIds: assetId,
  isDrilldown: true,
})

/**
 * Alphabetical, as the picker shows them. Built on first use, not at import: these insights
 * import `@/components/ui`, whose barrel imports the asset detail tabs and so this module, and
 * at import time the definitions may not be initialised yet.
 */
let historyOptions: HistoryOption[] | undefined
const getHistoryOptions = (): HistoryOption[] =>
  (historyOptions ??= [
    option('Alarms', ReeferAlarmDetails, alarmParams, 'No alarms for this period.'),
    // The API only returns alerts the viewer is subscribed to.
    option('Alerts', AlertsDetail, alertParams, 'No alerts you are subscribed to for this period.'),
    option('Door Activities', DoorActivities, asIs, 'No door activities for this period.'),
    option('Location', LocationHistory, asIs),
    option('Reefer Fridge Data', ReeferFridgeDataHistory, asIs),
    option('Reefer Operation', ReeferOperationHistory, asIs),
    option('Temperature', TemperatureHistory, asIs),
  ])

/**
 * The History tab of the asset detail sheet and modal: a picker of per-asset insights, each
 * for the same period. Like the Route Map tab it opens on today and can step a day at a time or
 * show any From/To range; the period is kept when switching insight.
 */
export const AssetInsightHistory = ({
  companyId,
  assetId,
  assetWindow,
}: {
  companyId: number
  assetId: number
  assetWindow: AssetWindowState
}) => {
  const options = getHistoryOptions()
  const { period } = assetWindow
  const [slug, setSlug] = useState(options[0].slug)
  const selected = options.find((o) => o.slug === slug) ?? options[0]
  const params = useMemo(() => assetWindowParams(companyId, assetId, period), [companyId, assetId, period])

  return (
    <div className="flex h-full flex-col gap-2">
      <AssetWindowNav
        assetWindow={assetWindow}
        leading={
          <select
            // h-8: as tall as the date pickers beside it.
            className="select select-sm h-8 w-fit text-xs"
            value={selected.slug}
            onChange={(e) => setSlug(e.target.value)}
            title="History"
            aria-label="History"
          >
            {options.map((o) => (
              <option key={o.slug} value={o.slug}>
                {o.label}
              </option>
            ))}
          </select>
        }
      />
      {/* Keyed so each insight starts fresh, with its own first-load skeleton. */}
      <div key={selected.slug} className="min-h-0 flex-1 overflow-auto">
        {selected.render(params)}
      </div>
    </div>
  )
}
